<template>
  <div class="class-table-wrapper" @click="handleCardClickEvent">
    <div class="table-head">
      <div class="title-wrapper">
        <div v-if="tagInfo.tagName" class="tag" :class="[tagInfo.className]">
          {{ tagInfo.tagName }}
        </div>
        <div class="title text-overflow-ellipsis">
          {{ cardData.title }}
        </div>
      </div>
      <div class="price-wrapper" :class="[fullClassName]">
        <div v-if="isGroupBuy" class="group-buy">
          {{ $t('featureComponents.ClassCard.groupBuy') }}
        </div>
        <template v-if="isClassFree">
          <div class="free-price">
            {{ $t('coursesDetail.free') }}
          </div>
        </template>
        <template v-else>
          <template v-if="isGroupBuy">
            <div class="discount-price">
              {{ currency }}{{ showPrice | priceUnitConvert(minorUnit, isKeepTwoDecimal) }}
            </div>
            <div class="sell-price">
              {{ currency }}{{ (showPerLesson ? cardData.activityItem.perShowPrice : cardData.activityItem.showPrice) | priceUnitConvert(minorUnit, isKeepTwoDecimal) }}
            </div>
            <div v-if="showPerLesson" class="lesson-name">
              {{ $t('courses.perLesson' ) }}
            </div>
          </template>
          <template v-else>
            <div v-if="isShowOriginalPrice" class="discount-price">
              {{ currency }}{{ showOrgPrice | priceUnitConvert(minorUnit, isKeepTwoDecimal) }}
            </div>
            <div class="sell-price">
              {{ currency }}{{ showPrice | priceUnitConvert(minorUnit, isKeepTwoDecimal) }}
            </div>
            <div v-if="showPerLesson" class="lesson-name">
              {{ $t('courses.perLesson' ) }}
            </div>
          </template>
        </template>
      </div>
    </div>
    <div class="table-body">
      <div class="field-group">
        <div v-if="gradeName" class="field-item">
          <div class="iconfont icon-book" />
          <div class="grade-name text-overflow-ellipsis" :title="gradeName">
            {{ gradeName }}
          </div>
        </div>
        <div class="field-item">
          <div class="iconfont icon-courses-type" />
          <div class="courses-type text-overflow-ellipsis">
            {{ cardData.spec.courseTypeName }}
          </div>
        </div>
        <div v-if="levelDegreeStar" class="field-item">
          <div class="iconfont icon-level-degree" />
          <div class="level-degree">
            <Rate
              icon-size="16px"
              on-color="#ffaa0a"
              void-color="#ffaa0a"
              :rate-level="Math.min(levelDegreeStar, 5)"
              :max="Math.min(levelDegreeStar, 5)"
            />
          </div>
        </div>
        <div class="field-item">
          <div class="iconfont icon-many-people" />
          <div class="spaces">
            {{ $tc('courses.classStore', cardData.store, { store: cardData.store }) }}
          </div>
        </div>
        <div v-if="teacherNames" class="field-item">
          <div class="iconfont icon-teacher" />
          <div class="teacher-names text-overflow-ellipsis" :title="teacherNames">
            {{ teacherNames }}
          </div>
        </div>
      </div>
      <div class="field-group">
        <div v-if="datetime" class="field-item">
          <div class="iconfont icon-clock-two" />
          <div class="datetime text-overflow-ellipsis" :title="datetime">
            {{ datetime }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import filters from 'common/filters'
import classCardMixin from 'mixins/courses/classCard'
import coursesSa from 'mixins/courses/sa.js';
import Rate from 'components/common/Rate'

export default {
  components: {
    Rate,
  },
  mixins: [classCardMixin, coursesSa],
  props: {
    // 班级卡片信息
    cardData: {
      default: () => ({}),
      type: Object,
    },

    // 是否是团购
    groupBuy: {
      default: () => ({}),
      type: Object,
    },

    // 卡片标识类型
    tagType: {
      default: 0,
      type: Number,
    },

    // 1,"Dual Online" 2,"Dual Offline" 3,"In-Person" 4,"Big_Online" 5,"Trial Class" 6,"Zoom" 7,"Class In"
    platformType: {
      default: '',
      type: String,
    },

    // 0大班 1小班
    subPlatformType: {
      default: 0,
      type: Number,
    },

    // 班级卡片来源模式
    // card/list
    coursesMode: {
      default: 'none',
      type: String,
    },
  },
  data() {
    return {
      dateFormat: this.$t('common.dateFormat'),
      isKeepTwoDecimal: this.$t('common.isKeepTwoDecimal'),
      pageConfig: this.$t('page.classCard'),
    }
  },
  computed: {
    locale() {
      return this.$store.state.locale
    },

    timezone() {
      return this.$store.state.timezone
    },

    currency() {
      return this.$store.state.currency.symbol
    },

    minorUnit() {
      return this.$store.state.currency.minorUnit;
    },

    // 标签信息
    tagInfo() {
      return {
        className: this.cardRightTagClass,
        tagName: this.cardRightTagText,
      }
    },

    // 年级名称
    gradeName() {
      const { gradeName = '' } = this.cardData.spec || {}
      return gradeName
    },

    // 难度等级
    levelDegreeStar() {
      const { levelDegreeStar = 0 } = this.cardData.spec || {}
      return levelDegreeStar
    },

    // 主讲老师名称，多个老师使用&隔开
    teacherNames() {
      const { teacherList = [] } = this.cardData.spec || {}
      if (!teacherList.length) {
        return ''
      }
      const teachers = teacherList.map((item) => item.sysName)
      return teachers.join('&')
    },

    // 判断开始日期和结束日期是否同一天
    isSameDay() {
      const { startDate = '', endDate = '' } = this.cardData.spec || {}
      return startDate.split(' ')[0] === endDate.split(' ')[0]
    },

    // 日期时间字符串
    datetime() {
      const { startDate = '', endDate = '' } = this.cardData.spec || {}
      const isSameDay = startDate.split(' ')[0] === endDate.split(' ')[0]
      const startDateFormate = filters.resourceDateFormate(startDate, this.timezone, this.dateFormat)
      const endDateFormate = filters.resourceDateFormate(endDate, this.timezone, this.dateFormat)
      const datetime = `${startDateFormate}${!isSameDay ? ' - ' : ''}${!isSameDay ? endDateFormate : ''} ${this.classTimeDesc}`
      return datetime || ''
    },

    // 满班样式名
    fullClassName() {
      return this.tagType === 15 ? 'full' : ''
    },

    // 是否显示总Lesson
    showPerLesson() {
      return this.cardData.perShowPrice !== undefined
    },

    // 免费价
    isClassFree() {
      // eslint-disable-next-line eqeqeq
      return this.cardData.showPrice == 0
    },

    // 是否显示原价
    isShowOriginalPrice() {
      let diffVal = this.cardData.showOrgPrice - this.cardData.showPrice
      if (Number.isNaN(diffVal)) diffVal = this.cardData.orgPrice - this.cardData.sellPrice
      if (Number.isNaN(diffVal)) return false
      return diffVal > 0
    },

    // 原价
    showOrgPrice() {
      if (this.showPerLesson) return this.cardData.perOrgPrice || this.cardData.showOrgPrice || this.cardData.orgPrice
      return this.cardData.showOrgPrice || this.cardData.orgPrice
    },

    // 销售价
    showPrice() {
      if (this.showPerLesson) return this.cardData.perShowPrice || this.cardData.showPrice || this.cardData.sellPrice
      return this.cardData.showPrice || this.cardData.sellPrice
    },

    // 是否是拼团
    isGroupBuy() {
      return Object.keys(this.groupBuy).length > 0
    },
  },
  mounted() {
    this.classCardShow()
  },
  methods: {
    // 卡片点击事件
    handleCardClickEvent() {
      const { id } = this.cardData
      if (!id) return
      const path = `/courses/detail/${id}`
      this.$router.push(path)
      this.classCardClick()
    },
    // 神策埋点-班级卡片曝光
    classCardShow() {
      const fromSourcePage = this.getClassFrom(this.$route.name)
      if (fromSourcePage) {
        this.Sensors.track('class_card_show', this.getClassSaData(fromSourcePage, this.cardData, this.coursesMode))
      }
    },
    // 神策埋点-班级卡片点击
    classCardClick() {
      const fromSourcePage = this.getClassFrom(this.$route.name)
      if (fromSourcePage) {
        this.Sensors.track('class_card_click', this.getClassSaData(fromSourcePage, this.cardData, this.coursesMode))
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.class-table-wrapper {
  padding: 30px 30px 26px 30px;
  background: #fff;
  border-radius: 10px;
  border: 1px solid rgba(222, 226, 231, 0.5);
  cursor: pointer;
}

.table-head {
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;

  .title-wrapper {
    display: flex;
    align-items: center;
    height: 20px;

    .tag {
      height: 20px;
      line-height: 20px;
      margin-right: 10px;
      padding: 0 10px;
      border-radius: 10px;
      border-bottom-right-radius: 0;
      font-size: 12px;
      color: #fff;
      font-weight: bold;
      background: #ffaa0a;

      &.red {
        background: #e91414;
      }

      &.orange {
        background: var(--txt-color-link);
      }

      &.green {
        background: #02ca8a;
      }

      &.blue {
        background: #3370ff;
      }

      &.pink {
        background: #d3d3d3;
      }

      &.grey {
        background: #ea2343;
      }
    }

    .title {
      width: 800px;
      height: 20px;
      line-height: 20px;
      font-size: 20px;
      color: var(--txt-color-lv1);
      font-weight: bold;
    }
  }

  .price-wrapper {
    display: flex;
    height: 20px;
    line-height: 20px;

    .group-buy {
      display: flex;
      align-items: center;
      justify-content: space-around;
      // width: 80px;
      padding: 0 12px;
      height: 20px;
      margin-right: 20px;
      background: linear-gradient(225deg, #ff3034 0%, #ff4286 100%);
      border-radius: 4px;
      font-size: 12px;
      font-weight: bold;
      color: #fff;
      line-height: 20px;
    }

    .discount-price {
      margin: 2px 10px 0 0;
      font-size: 12px;
      color: var(--txt-color-lv2);
      font-weight: bold;
      text-decoration: line-through;
    }

    .lesson-name {
      margin: 2px 0 0 5px;
      font-size: 12px;
      color: var(--txt-color-link);
      font-weight: bold;
    }

    .free-price,
    .sell-price {
      font-size: 20px;
      color: var(--txt-color-link);
      font-weight: bold;
    }

    &.full {
      .free-price,
      .sell-price {
        color: var(--txt-color-lv2);
      }
    }
  }
}

.table-body {
  display: flex;
  justify-content: space-between;

  .field-group {
    display: flex;
  }

  .field-item {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-right: 20px;
    font-size: 14px;
    color: var(--txt-color-lv2);

    .iconfont {
      margin-right: 8px;
      color: #aeaeae;
    }

    &:last-child {
      margin-right: 0;
    }
  }

  .grade-name,
  .courses-type,
  .spaces {
    width: 120px;
  }

  .level-degree {
    width: 100px;
    margin-top: -2px;
  }

  .teacher-names {
    width: 200px;
  }

  .datetime {
    width: 280px;
  }
}
</style>
